import { useEffect, useRef, useState } from "react";
import { X } from "lucide-react";
import { AcDialog, Button, EmptyState, Icon, Sheet, Spinner } from "@cntrl/ui";
import { nfcAvailable, nfcWriteUrl } from "@cntrl/native";
import { PUBLIC_WEB_URL } from "../../config/env.js";
import {
  useRoadCase,
  useCreateCase,
  useRenameCase,
  useDeleteCase,
  useAddCaseItem,
  useDeleteCaseItem,
  usePairCase,
  useUnpairCase,
  useMarkWritten,
} from "../../app/hooks/useRoadCases.js";
import { ApiError } from "../../app/hooks/apiError.js";
import { pushToast } from "../../app/toasts.js";
import styles from "./roadCases.module.scss";

/** The absolute sticker URL. Built on the PUBLIC-WEB origin (the host serving the AASA + the SSR /scan page), never
 * window.location.origin: on native the app runs at https://localhost, and a localhost URL burned onto a sticker is
 * a dead universal link. Falls back to the origin for a same-origin web build. */
export function stickerUrl(scanPath: string): string {
  const base = PUBLIC_WEB_URL || (typeof window !== "undefined" ? window.location.origin : "");
  return base + scanPath;
}

/**
 * The CASE SHEET (ART-253, legacy `caseSheetHtml`): the case's name, its sticker in one of three states — not
 * paired · token ready but not written (amber) · paired — what is packed in it, and Delete case. Destructive acts
 * (delete, discard / remove a sticker) confirm first: a removed sticker stops resolving immediately, and a case on
 * the truck with a dead sticker is found out at the next load-in.
 *
 * `caseId={null}` is the NEW-case sheet (PO, ART-253): name, contents and sticker in ONE popup, where it used to be a
 * name-only inline field and everything else afterwards. The name and items are a local draft until the case is
 * needed — "Add case", or "Pair NFC sticker", which creates it and goes straight to the iPhone's write prompt. From
 * that moment the sheet is the ordinary case sheet for the case it made.
 */
export function CaseSheet({ caseId: initialId, onClose }: { caseId: string | null; onClose: () => void }) {
  const [caseId, setCaseId] = useState<string | null>(initialId);
  const creating = caseId === null;
  const q = useRoadCase(caseId);
  const create = useCreateCase();
  const rename = useRenameCase();
  const del = useDeleteCase();
  const addItem = useAddCaseItem();
  const delItem = useDeleteCaseItem();
  const pair = usePairCase();
  const unpair = useUnpairCase();
  const markWritten = useMarkWritten();
  const [name, setName] = useState("");
  const [newItem, setNewItem] = useState("");
  const [confirm, setConfirm] = useState<"delete" | "unpair" | null>(null);
  const [writing, setWriting] = useState(false);
  const [draftItems, setDraftItems] = useState<string[]>([]);
  // One create, however many buttons are pressed while it is in flight.
  const creatingCase = useRef<Promise<string> | null>(null);

  const detail = q.data;
  const serverName = detail?.case?.name ?? "";
  useEffect(() => setName(serverName), [serverName]);

  const commitName = () => {
    const n = name.trim();
    if (!caseId) return; // a new case is named by its create
    if (!n || n === serverName) {
      setName(serverName);
      return;
    }
    rename.mutate({ caseId, name: n });
  };

  const submitItem = () => {
    const n = newItem.trim();
    if (!n) return;
    if (!caseId) {
      setDraftItems((d) => [...d, n]);
      setNewItem("");
      return;
    }
    addItem.mutate({ caseId, name: n }, { onSuccess: () => setNewItem("") });
  };

  /** The case this sheet edits, creating it (with the drafted items) the first time something needs it. */
  const ensureCase = (): Promise<string> => {
    if (caseId) return Promise.resolve(caseId);
    creatingCase.current ??= (async () => {
      const row = await create.mutateAsync(name.trim());
      const id = row.caseId!;
      for (const item of draftItems) await addItem.mutateAsync({ caseId: id, name: item });
      setDraftItems([]);
      setCaseId(id);
      return id;
    })().finally(() => {
      creatingCase.current = null;
    });
    return creatingCase.current;
  };

  const pairSticker = async () => {
    try {
      const id = await ensureCase();
      const r = await pair.mutateAsync(id);
      if (r.scanPath) await write(r.scanPath, id);
    } catch {
      // each mutation already toasted its own failure
    }
  };

  const addCase = async () => {
    try {
      await ensureCase();
      onClose();
    } catch {
      // toasted by the create / add-item mutation; the draft stays so nothing typed is lost
    }
  };

  const write = async (scanPath: string, id: string | null = caseId) => {
    const url = stickerUrl(scanPath);
    if (!nfcAvailable()) {
      try {
        await navigator.clipboard.writeText(url);
        pushToast("Sticker link copied — write it with any NFC app, then tap Mark written.", "info");
      } catch {
        pushToast("Copy failed — long-press the link to copy it.");
      }
      return;
    }
    setWriting(true);
    try {
      await nfcWriteUrl(url);
      if (id) markWritten.mutate(id, { onSuccess: () => pushToast("Sticker written.", "success") });
    } catch (e) {
      pushToast(e instanceof Error ? e.message : "Couldn't write the sticker.");
    } finally {
      setWriting(false);
    }
  };

  const items = detail?.items ?? [];
  const named = name.trim().length > 0;
  const busy = create.isPending || pair.isPending || writing;
  const paired = detail?.case?.paired === true;
  const written = detail?.case?.written === true;
  const scanPath = detail?.scanPath ?? null;

  return (
    <Sheet title={creating ? "New case" : serverName || "Case"} onClose={onClose}>
      {!creating && q.isLoading ? (
        <div className={styles.center} role="status"><Spinner label="Loading case" /></div>
      ) : !creating && (q.isError || !detail) ? (
        <EmptyState
          title="Couldn't load this case"
          description={q.error instanceof ApiError ? q.error.message : "Try again in a moment."}
          action={<Button onClick={() => void q.refetch()}>Retry</Button>}
        />
      ) : (
        <div className={styles.sheet}>
          <header className={styles.sheetHead}>
            <span className={styles.eyebrow}>
              {creating ? "New case" : `Case · ${items.length} item${items.length === 1 ? "" : "s"}`}
            </span>
            <input
              className={styles.sheetTitle}
              value={name}
              maxLength={120}
              placeholder="Case name"
              // eslint-disable-next-line jsx-a11y/no-autofocus -- the new-case sheet exists to be typed into
              autoFocus={creating}
              aria-label="Case name"
              onChange={(e) => setName(e.target.value)}
              onBlur={creating ? undefined : commitName}
              onKeyDown={(e) => {
                if (e.key === "Enter") e.currentTarget.blur();
              }}
            />
          </header>

          {/* The sticker, in legacy's three states. */}
          {!paired ? (
            <button
              type="button"
              className={styles.nfc}
              data-nfc="none"
              disabled={busy || (creating && !named)}
              onClick={() => void pairSticker()}
            >
              <span className={styles.nfcLabel}>Pair NFC sticker</span>
            </button>
          ) : !written ? (
            <div className={styles.nfc} data-nfc="pending">
              <div className={styles.nfcBody}>
                <span className={styles.nfcLabel}>Token ready · sticker not written</span>
                <span className={styles.nfcToken}>{detail?.token}</span>
              </div>
              <div className={styles.nfcActions}>
                {scanPath && (
                  <Button size="sm" loading={writing} onClick={() => void write(scanPath)}>
                    {nfcAvailable() ? "Write" : "Copy link"}
                  </Button>
                )}
                {!nfcAvailable() && caseId && (
                  <Button size="sm" variant="ghost" onClick={() => markWritten.mutate(caseId)}>Mark written</Button>
                )}
                <Button size="sm" variant="ghost" onClick={() => setConfirm("unpair")}>Discard</Button>
              </div>
            </div>
          ) : (
            <div className={styles.nfc} data-nfc="paired">
              <div className={styles.nfcBody}>
                <span className={styles.nfcLabel}>NFC sticker paired</span>
                <span className={styles.nfcToken}>{detail?.token}</span>
              </div>
              <div className={styles.nfcActions}>
                <Button size="sm" variant="ghost" onClick={() => setConfirm("unpair")}>Remove</Button>
              </div>
            </div>
          )}

          <ul className={styles.items}>
            {creating ? (
              draftItems.length === 0 ? (
                <li className={styles.itemsEmpty}>No items yet.</li>
              ) : (
                draftItems.map((it, i) => (
                  <li key={`${i}-${it}`} className={styles.item}>
                    <span className={styles.itemDot} aria-hidden="true" />
                    <span className={styles.itemName}>{it}</span>
                    <button
                      type="button"
                      className={styles.itemDel}
                      aria-label={`Remove ${it}`}
                      onClick={() => setDraftItems((d) => d.filter((_, j) => j !== i))}
                    >
                      <Icon icon={X} size={16} />
                    </button>
                  </li>
                ))
              )
            ) : items.length === 0 ? (
              <li className={styles.itemsEmpty}>No items yet.</li>
            ) : (
              items.map((it) => (
                <li key={it.itemId} className={styles.item}>
                  <span className={styles.itemDot} aria-hidden="true" />
                  <span className={styles.itemName}>{it.name}</span>
                  <button
                    type="button"
                    className={styles.itemDel}
                    aria-label={`Remove ${it.name}`}
                    onClick={() => it.itemId && caseId && delItem.mutate({ caseId, itemId: it.itemId })}
                  >
                    <Icon icon={X} size={16} />
                  </button>
                </li>
              ))
            )}
          </ul>

          <div className={styles.addRow}>
            <input
              className={styles.addInput}
              placeholder="Add item (e.g. Kick pedal)"
              aria-label="New item"
              value={newItem}
              maxLength={120}
              onChange={(e) => setNewItem(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") submitItem();
              }}
            />
            {/* Outlined, so the footer's Done / Add case is the sheet's one filled action — two filled buttons stacked
                on the same edge read as equals and invite the wrong tap (PO, ART-253). */}
            <Button size="sm" variant="ghost" onClick={submitItem} disabled={!newItem.trim() || addItem.isPending}>
              + Add
            </Button>
          </div>

          <div className={styles.sheetFoot}>
            {creating ? (
              <>
                <Button variant="ghost" onClick={onClose}>Cancel</Button>
                <Button onClick={() => void addCase()} disabled={!named} loading={create.isPending}>Add case</Button>
              </>
            ) : (
              <>
                <Button variant="ghost" tone="danger" onClick={() => setConfirm("delete")}>Delete case</Button>
                <Button onClick={onClose}>Done</Button>
              </>
            )}
          </div>
        </div>
      )}

      {confirm === "delete" && (
        <AcDialog
          title="Delete this case?"
          message={`${serverName} and its item list are removed, and its sticker stops working.`}
          confirmLabel="Delete case"
          tone="danger"
          loading={del.isPending}
          onCancel={() => setConfirm(null)}
          onConfirm={() =>
            caseId &&
            del.mutate(caseId, {
              onSuccess: () => {
                setConfirm(null);
                onClose();
              },
            })
          }
        />
      )}
      {confirm === "unpair" && (
        <AcDialog
          title={written ? "Remove this sticker?" : "Discard this sticker code?"}
          message="The sticker stops working immediately. You can pair a new one afterwards."
          confirmLabel={written ? "Remove" : "Discard"}
          tone="danger"
          loading={unpair.isPending}
          onCancel={() => setConfirm(null)}
          onConfirm={() => caseId && unpair.mutate(caseId, { onSuccess: () => setConfirm(null) })}
        />
      )}
    </Sheet>
  );
}
